import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
// import { RenderConfidenceInterval } from './ConfidenceInterval';TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';TODO: MVP-5260 - Bring Confidence Interval back to charts
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps } from './types';
import {
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForPotentialGraph,
  kgToTonneFormatter,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const carbonColors = getColorForIndicator(INDICATOR.CARBON);

const chartColors = {
  carbon: carbonColors,
  confidenceInterval: {
    fill: carbonColors.confidenceInterval,
  },
} as const;

const totalFactNames = {
  carbon_storage: 'cbg',
  permanent_soil_carbon_total_benchmark_low: 'cbg_benchmark_low',
  permanent_soil_carbon_total_benchmark_moderate: 'cbg_benchmark_moderate',
  permanent_soil_carbon_total_benchmark_high: 'cbg_benchmark_high',
};

const perHaFactNames = {
  carbon_storage_per_ha: 'cbg',
  permanent_soil_carbon_per_ha_benchmark_low: 'cbg_benchmark_low',
  permanent_soil_carbon_per_ha_benchmark_moderate: 'cbg_benchmark_moderate',
  permanent_soil_carbon_per_ha_benchmark_high: 'cbg_benchmark_high',
};

export const CarbonStorageBGPotentialChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps> = ({
  analysisType,
  data,
  benchmarks,
  className,
  showTooltip,
  zoom,
  setZoom,
  ...delegated
}) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, 0.8, 1.2, kgToTonneFormatter);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? t('global.ui.unit.t') : t('global.ui.unit.t/ha');

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames, kgToTonneFormatter);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }
  const xTicksDomain = getXAxisDomainForPotentialGraph(firstTick.label, lastTick.label);
  const xAxisProps = getXAxisProps(xTicksDomain);

  let chartData = extrapolateReferenceLinesChartData(xTicksDomain, sortedChartData, 'cbg_benchmark_low');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'cbg_benchmark_moderate');
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, 'cbg_benchmark_high');

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain: undefined,
    formatter: kgToTonneFormatter,
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          cbg: {
            label: t('global.analysis.belowGroundCarbonStorage'),
            color: chartColors.carbon.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='cbg' x1='0' y1='-2.6' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.carbon.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.carbon.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'cbg.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'cbg-potential-confidence-interval',
          })} */}
          <Area
            id='cbg-potential-area'
            type='monotone'
            stroke='var(--color-cbg)'
            strokeWidth={2}
            fill='url(#cbg)'
            fillOpacity={1}
            dataKey='cbg.value'
            connectNulls
            activeDot={ChartTooltipDot}
          />
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_low.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_low) ? t('global.benchmark.low') : null,
            chartData,
            id: 'cbg-potential-benchmark-low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_moderate.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_moderate) ? t('global.benchmark.moderate') : null,
            chartData,
            id: 'cbg-potential-benchmark-moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'cbg_benchmark_high.value',
            type: 'monotone',
            label: chartData.some((point) => !!point.cbg_benchmark_high) ? t('global.benchmark.high') : null,
            chartData,
            id: 'cbg-potential-benchmark-high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis className='cbg-potential-x-axis' {...xAxisProps} />
          <Chart.YAxis domain={yAxisDomain} className='cbg-potential-y-axis' allowDataOverflow={true}>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => <CustomTooltip {...props} unit={unit as UnitEnum} benchmarks={benchmarks} />}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('global.analysis.belowGroundCarbonStorage'),
      color: chartColors.carbon.fill,
    },
    // ...confidenceIntervalLegendItem,
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' data-testid='carbon-storage-bg-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const cbgValue = payload?.find((data) => data.dataKey === 'cbg.value');
  const cbgDisplay = !isNullish(cbgValue?.value)
    ? `${getDisplayNumber(cbgValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');
  const cbgBenchmarkType = useBenchmarkForFact(benchmarks, cbgValue?.value);
  const dataPointHasLabel = cbgValue && cbgValue?.payload?.cbg?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? cbgValue.payload.cbg.labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {cbgBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={cbgBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.carbonStorageBg')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{cbgDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={cbgValue?.payload?.cbg?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipCarbonStorageBGPotential };
